import { useEffect, type RefObject } from "react";
import { GRID_STEP, growToStep, mod, pushToLine, setGridOrigin, trimToLine } from "../lib/gridSnap";

// Lays every sheet on the page onto the engineering-paper grid: its left and top edges on a line,
// its width and height a whole number of cells, so all four edges sit on the grid.
//
// A sheet is anything styled with `card` / `emptyFrame` from theme.ts (they carry the `--sheet`
// marker) or marked `data-sheet`. Only the outermost sheet is moved — a sheet inside a sheet keeps
// its parent's layout — and nothing inside a scrolling region, a positioned overlay or a
// `data-grid-snap="off"` subtree is touched.
//
// The pass only ever adds: margin on the left and top, max-width to bring the right edge back to a
// line, min-height to the next whole cell. It resets what it wrote before measuring again, and
// notices when React has since changed one of those properties itself, so a page's own layout
// always wins over a stale snap.

const SHEET_SELECTOR = '[data-sheet], [style*="--sheet"]';
const PROPS = ["marginTop", "marginLeft", "minHeight", "maxWidth", "rowGap"] as const;
type Prop = (typeof PROPS)[number];

interface Record_ {
  base: Partial<Record<Prop, string>>;
  wrote: Partial<Record<Prop, string>>;
}

const records = new WeakMap<HTMLElement, Record_>();

function recordFor(el: HTMLElement): Record_ {
  let rec = records.get(el);
  if (!rec) {
    rec = { base: {}, wrote: {} };
    for (const p of PROPS) rec.base[p] = el.style[p];
    records.set(el, rec);
  }
  return rec;
}

function reset(el: HTMLElement): void {
  const rec = recordFor(el);
  for (const p of PROPS) {
    const wrote = rec.wrote[p];
    if (wrote !== undefined) {
      // React rewrote the property since the last pass: that value is now the page's own.
      if (el.style[p] !== wrote) rec.base[p] = el.style[p];
      el.style[p] = rec.base[p] ?? "";
      delete rec.wrote[p];
    } else {
      rec.base[p] = el.style[p];
    }
  }
}

/** Everything a pass has written to, so the next pass can put it back before measuring. */
const touched = new Set<HTMLElement>();

function write(el: HTMLElement, p: Prop, px: number): void {
  el.style[p] = `${Math.round(px * 100) / 100}px`;
  recordFor(el).wrote[p] = el.style[p];
  touched.add(el);
}

function resetTouched(): void {
  for (const el of Array.from(touched)) {
    reset(el);
    touched.delete(el);
  }
}

/** Whether the element sits in normal flow under `root`, outside anything that scrolls or floats. */
function snappable(el: HTMLElement, root: HTMLElement, cache: Map<HTMLElement, boolean>): boolean {
  const own = getComputedStyle(el);
  if (own.position === "absolute" || own.position === "fixed" || own.position === "sticky") return false;
  if (own.display === "none" || own.display === "contents" || own.display.startsWith("inline") || own.display.startsWith("table")) return false;
  let node = el.parentElement;
  while (node && node !== root) {
    let ok = cache.get(node);
    if (ok === undefined) {
      const cs = getComputedStyle(node);
      ok = !node.matches(SHEET_SELECTOR)
        && node.dataset.gridSnap !== "off"
        && cs.overflowX === "visible" && cs.overflowY === "visible"
        && cs.position !== "fixed" && cs.position !== "absolute" && cs.position !== "sticky";
      cache.set(node, ok);
    }
    if (!ok) return false;
    node = node.parentElement;
  }
  return node === root;
}

/**
 * The gap above a sheet as layout actually draws it. In block flow a sheet's top margin collapses
 * with the bottom margin of the block before it, so adding a few pixels to the smaller of the two
 * moves nothing: start from the larger.
 */
function effectiveMarginTop(el: HTMLElement): number {
  const own = parseFloat(getComputedStyle(el).marginTop) || 0;
  const prev = el.previousElementSibling as HTMLElement | null;
  const parent = el.parentElement;
  if (!prev || !parent || own < 0) return own;
  const display = getComputedStyle(parent).display;
  if (display !== "block" && display !== "flow-root" && display !== "list-item") return own;
  const prevBottom = parseFloat(getComputedStyle(prev).marginBottom) || 0;
  return Math.max(own, prevBottom);
}

/** One pass over `root`: exported for checking a layout by hand; the shell uses useGridSnap. */
export function snapPass(root: HTMLElement): void {
  // Resetting every sheet before measuring briefly shortens the page, and the browser clamps (or
  // anchors) the scroll position to fit. Put it back once the sheets are re-laid, or every pass —
  // one per second under a ticking counter — moves the page under the reader.
  const scrollTop = root.scrollTop;
  try {
    layOut(root);
  } finally {
    if (root.scrollTop !== scrollTop) root.scrollTop = scrollTop;
  }
}

function layOut(root: HTMLElement): void {
  const candidates = Array.from(root.querySelectorAll<HTMLElement>(SHEET_SELECTOR));
  const cache = new Map<HTMLElement, boolean>();
  const sheets = candidates.filter((el) => snappable(el, root, cache));
  resetTouched();
  sheets.forEach(reset);
  if (sheets.length === 0) return;

  const rootRect = root.getBoundingClientRect();
  const measure = () => sheets.map((el) => {
    const r = el.getBoundingClientRect();
    return {
      left: r.left - rootRect.left + root.scrollLeft,
      top: r.top - rootRect.top + root.scrollTop,
      width: r.width,
      height: r.height,
    };
  });

  let rects = measure();
  const visible = rects.map((r) => r.width >= 48 && r.height > 0);
  const shown = rects.filter((_, i) => visible[i]);
  if (shown.length === 0) return;
  const ox = mod(Math.min(...shown.map((r) => r.left)), GRID_STEP);
  const oy = mod(Math.min(...shown.map((r) => r.top)), GRID_STEP);
  setGridOrigin(ox, oy);
  root.style.setProperty("--grid-x", `${ox}px`);
  root.style.setProperty("--grid-y", `${oy}px`);

  // Left edges onto a line, right edges back to one. In a flex row, trimming one sheet lets its
  // neighbour grow, so repeat until the row settles. A centred sheet is pinned where it is this
  // pass; the next pass (on resize) starts again from its auto margins.
  for (let i = 0; i < 4; i++) {
    const margins = sheets.map((el) => parseFloat(getComputedStyle(el).marginLeft) || 0);
    let moved = false;
    sheets.forEach((el, k) => {
      if (!visible[k]) return;
      const d = pushToLine(rects[k].left, ox);
      if (d > 0) { write(el, "marginLeft", margins[k] + d); moved = true; }
    });
    if (moved) rects = measure();
    sheets.forEach((el, k) => {
      if (!visible[k]) return;
      const d = trimToLine(rects[k].left + rects[k].width, ox);
      if (d > 0 && rects[k].width - d >= 48) { write(el, "maxWidth", rects[k].width - d); moved = true; }
    });
    if (!moved) break;
    rects = measure();
  }

  // Rows of sheets in a grid or a wrapping flex container: a 12 px row gap can never put the second
  // row on a line, so round the gap up to a whole cell.
  const containers = new Set<HTMLElement>();
  sheets.forEach((el, k) => {
    const box = verticalBox(el, root);
    const host = box !== el && isWrapper(box) ? box.parentElement : el.parentElement;
    if (visible[k] && host && host !== root) containers.add(host);
  });
  for (const host of containers) {
    const cs = getComputedStyle(host);
    if (!cs.display.includes("grid") && !(cs.display.includes("flex") && cs.flexWrap === "wrap")) continue;
    const gap = parseFloat(cs.rowGap) || 0;
    if (gap > 0 && gap % GRID_STEP !== 0) { reset(host); write(host, "rowGap", growToStep(gap)); }
  }

  // A sheet that fills its parent (height: 100%) is moved through that parent: a margin on the
  // sheet itself would push it out of the bottom. When the parent is only a wrapper (a link round
  // a tile) the wrapper is grown too; inside a grid or flex container the sheet grows itself and
  // its row follows.
  const boxes = sheets.map((el) => verticalBox(el, root));
  const growers = sheets.map((el, k) => (boxes[k] !== el && isWrapper(boxes[k]) ? boxes[k] : el));
  const measureEls = (els: HTMLElement[]) => els.map((el) => {
    const r = el.getBoundingClientRect();
    return { top: r.top - rootRect.top + root.scrollTop, height: r.height };
  });
  const measureBoxes = () => measureEls(boxes);

  // Heights: a whole number of cells, so a sheet that starts on a line also ends on one.
  const growRects = measureEls(growers);
  growers.forEach((el, k) => {
    if (!visible[k]) return;
    const target = growToStep(growRects[k].height);
    if (target - growRects[k].height > 0.5) write(el, "minHeight", target);
  });
  let boxRects = measureBoxes();

  // Tops, top to bottom. Moving a row down moves everything below it in the same flow, so carry
  // that shift into the next row's predicted position; then measure again and correct whatever
  // did not move as predicted (a neighbouring column, a collapsed margin).
  for (let pass = 0; pass < 6; pass++) {
    boxRects = measureBoxes();
    const order = boxes
      .map((box, k) => ({ box, k }))
      .filter(({ k }) => visible[k])
      .sort((a, b) => boxRects[a.k].top - boxRects[b.k].top || a.k - b.k);
    const margins = new Map(order.map(({ box }) => [box, effectiveMarginTop(box)]));
    const done = new Set<HTMLElement>();
    let shift = 0, rowTop = -Infinity, rowShift = 0, moved = false;
    for (const { box, k } of order) {
      if (done.has(box)) continue;
      done.add(box);
      const top = boxRects[k].top;
      if (top - rowTop >= 1) { shift += rowShift; rowShift = 0; rowTop = top; }
      const d = pushToLine(top + shift, oy);
      if (d > 0) {
        write(box, "marginTop", (margins.get(box) ?? 0) + d);
        rowShift = Math.max(rowShift, d);
        moved = true;
      }
    }
    if (!moved) break;
  }
}

/** The element whose top margin decides where a sheet sits vertically. */
function verticalBox(el: HTMLElement, root: HTMLElement): HTMLElement {
  const parent = el.parentElement;
  if (parent && parent !== root && el.style.height.endsWith("%")) return parent;
  return el;
}

/** A parent that only wraps one sheet (a link or a div round a tile), not a layout container. */
function isWrapper(el: HTMLElement): boolean {
  const display = getComputedStyle(el).display;
  return el.children.length === 1 && !display.includes("grid") && !display.includes("flex");
}

/**
 * Keep the sheets inside `rootRef` (the shell's scrolling <main>) on the grid: on mount, whenever
 * the page's content changes and whenever the main area is resized.
 */
export function useGridSnap(rootRef: RefObject<HTMLElement | null>, enabled = true): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return;

    // A timer, not requestAnimationFrame: a page opened in a background tab must still be laid
    // out on the grid by the time it is looked at, and frames do not run in a hidden tab.
    let timer: number | undefined;
    let lastRun = 0;
    const watched = new Set<Element>();
    const run = () => {
      timer = undefined;
      lastRun = performance.now();
      snapPass(root);
      // Watch the page's own wrapper too: content that grows without adding elements (a font, an
      // image, a style change) moves the sheets below it. A pass that changes sizes is seen once
      // more, and the pass after it changes nothing, so this settles.
      for (const child of Array.from(root.children)) {
        if (!watched.has(child)) { watched.add(child); resize.observe(child); }
      }
      for (const el of Array.from(watched)) {
        if (!root.contains(el)) { watched.delete(el); resize.unobserve(el); }
      }
    };
    const schedule = () => {
      if (timer !== undefined) return;
      // Throttled: a ticking counter on the page must not re-lay the page every frame.
      timer = window.setTimeout(run, Math.max(80, 250 - (performance.now() - lastRun)));
    };

    // Our own writes are attribute changes, which are deliberately not observed.
    const mutations = new MutationObserver(schedule);
    mutations.observe(root, { childList: true, subtree: true, characterData: true });
    const resize = new ResizeObserver(schedule);
    resize.observe(root);
    watched.add(root);
    document.fonts?.ready.then(schedule).catch(() => undefined);
    schedule();

    return () => {
      mutations.disconnect();
      resize.disconnect();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [rootRef, enabled]);
}
